import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import PatientUtils from 'services/PatientUtils';
import EditAddress from 'components/Patient/EditAddress';
import PhysicianUtils from 'services/PhysicianUtils';
import Icon from 'components/Utils/NewIcon';
import AccountsList from 'components/Patient/AccountsList';
import { PatientImage } from 'components/Common/UserImage';
import DefaultModal from 'components/Utils/DefaultModal';
import { OhipModal } from 'components/Patient/OHIPFields';
import { getHealthCardLabel } from 'services/portalData';
import SubscriptionUtils from 'services/SubscriptionUtils';
import StaffSearchModal from 'components/Modals/StaffSearchModal';
import './style.scss';
import DefaultButton from 'components/Utils/NewDefaultButton';
import TextareaAutosize from 'components/Common/TextAreaAutosize';
import CalendarUtils from 'services/CalendarUtils';
import RouteUtils from 'services/RouteUtils';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import { useNavStack } from 'careand-redux/slices/ui/hooks';
import { Stacks } from 'careand-redux/slices/ui/navigation';
import DefaultTooltip from 'components/Utils/DefaultTooltip';
import { IsStripeEnabled } from 'careand-redux/selectors';

const PatientInfoRow = (props) => {
  const { patient } = props;
  const patientId = patient?.id;
  const dispatch = useAppDispatch();

  const primaryPhysician = useAppSelector(
    (state) => state.physicians.byId[patient.primary_physician],
  );

  const stripeEnabled = useAppSelector(IsStripeEnabled);
  const subscription = useAppSelector((state) => state.payments.subscriptions[patientId]);
  const isUnsubscribed = SubscriptionUtils.isUnsubscribed(subscription);
  const isOverdue = SubscriptionUtils.isPaymentOverdue(subscription);

  const subscriptionId = subscription?.id;

  useEffect(() => {
    if (stripeEnabled && patientId && !subscriptionId)
      dispatch(api.Payments.getUserSubscription({ userId: patientId }));
  }, [dispatch, stripeEnabled, patientId, subscriptionId]);

  if (!patient) return null;

  async function changePrimaryPhysician(physician) {
    let patientUpdate = {
      ...patient,
      primary_physician: physician.id,
      clinic_id: physician.clinic_id,
    };
    await dispatch(
      api.Patients.updatePatient({
        id: patientUpdate.id,
        patient: patientUpdate,
      }),
      {
        errorTitle: 'Primary Practitioner Update',
      },
    );
  }

  const willCancel = subscription?.cancel_at;
  const willCancelText = willCancel
    ? `This subscription is scheduled to end on ${CalendarUtils.format(subscription?.cancel_at)}.`
    : '';
  const willRenewText =
    willCancelText ||
    (subscription?.current_period_end
      ? `This subscription will renew on ${CalendarUtils.format(subscription?.current_period_end)}`
      : '');

  return (
    <div className="patient-info row">
      <PatientPhoto patient={patient} />
      <div className="details">
        <span className="name">
          {PatientUtils.getName(patient, undefined, undefined, undefined, true)}{' '}
          <GenderIcon patient={patient} />
        </span>
        <div className="row second-row">
          <div className="dob-element contents">
            <DateOfBirth patient={patient} />
          </div>
          {(!stripeEnabled || !isUnsubscribed) && (
            <PrimaryPhysicianElement
              primaryPhysician={primaryPhysician}
              changePrimaryPhysician={changePrimaryPhysician}
            />
          )}
          <OHIPElement patient={patient} />
          {stripeEnabled && (
            <div className="subscription-element contents">
              <Item
                title="Subscription"
                text={SubscriptionUtils.getSubscriptionName(subscription)}
                tooltip={willRenewText}
                extra={
                  isOverdue ? (
                    <span className="detail red-text">Overdue</span>
                  ) : willCancel ? (
                    <span className="detail yellow-text">
                      <Icon
                        src="exclamation-circle"
                        className="ml-1"
                        fontAwesomeStyle="solid"
                        showTooltip={true}
                        alt={willCancelText}
                        height="15px"
                        width="15px"
                      />
                    </span>
                  ) : null
                }
              />
            </div>
          )}
          <Item title="Phone" text={PatientUtils.getContact(patient)} showSeparator={true} />
          <AddressElement patient={patient} showSeparator={true} />
          <EmailElement patient={patient} />
        </div>
      </div>
    </div>
  );
};

PatientInfoRow.propTypes = {
  patient: PropTypes.object.isRequired,
};

export const EmailElement = React.memo(({ patient, showSeparator = false }) => (
  <div className="flex">
    <Item showSeparator={showSeparator} title="Email" text={patient.email} />
  </div>
));

export const AddressElement = React.memo(({ patient, showSeparator = false, className }) => {
  const [editAddress, setEditAddress] = useState(false);

  function closeModal() {
    setEditAddress(false);
  }

  function openModal() {
    setEditAddress(true);
  }

  return (
    <>
      {editAddress && (
        <DefaultModal
          className="modal edit-address-modal"
          key="edit-address-modal"
          title="Edit Address"
          wrap={true}
          padding={true}
          isOpen={editAddress}
          onRequestClose={closeModal}
          content={<EditAddress patient={patient} onSuccess={closeModal} />}
        />
      )}
      <div onClick={openModal} className="clickable flex">
        <Item
          showSeparator={showSeparator}
          title="Address"
          text=""
          extra={
            <ValidIcon
              className={className}
              value={PatientUtils.hasValidAddress(patient)}
              labelOk="Valid Address"
              labelNotOk="Invalid Address"
            />
          }
        />
      </div>
    </>
  );
});

const PrimaryPhysicianElement = React.memo(({ primaryPhysician, changePrimaryPhysician }) => {
  let [primaryPractitionerModal, setPrimaryPractitionerModal] = useState(false);

  function onSelect(physician) {
    changePrimaryPhysician(physician);
    closeModal();
  }

  function closeModal() {
    setPrimaryPractitionerModal(false);
  }

  function openModal() {
    setPrimaryPractitionerModal(true);
  }

  let text = primaryPhysician ? PhysicianUtils.getName(primaryPhysician) : 'N/A';

  return (
    <>
      {primaryPractitionerModal && (
        <DefaultModal
          key="physician-search-modal"
          content={
            <StaffSearchModal
              title="Change Primary Practitioner"
              ignoreAdmins={true}
              onSelect={onSelect}
              backButton={closeModal}
            />
          }
          isOpen={primaryPractitionerModal}
          onRequestClose={closeModal}
        />
      )}
      <Item title="Primary NP" text={text} onClick={openModal} />
    </>
  );
});

const EditPatientAlert = ({ patient }) => {
  const [alert, setAlert] = useState(patient.alert || '');
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();

  function onChange(event) {
    setAlert(event.target.value);
  }

  async function saveChanges() {
    setLoading(true);
    await dispatch(
      api.Patients.updatePatient({
        id: patient.id,
        patient: { ...patient, alert },
      }),
      { errorTitle: 'Update Patient' },
    );
    setLoading(false);
  }

  return (
    <div>
      <TextareaAutosize cacheMeasurements name="alert" value={alert} onChange={onChange} />
      <DefaultButton name="Save" loading={loading} onClick={saveChanges} />
    </div>
  );
};

export const OHIPElement = ({ patient, showSeparator }) => {
  let [ohipModal, showOhipModal] = useState(false);

  function openModal() {
    showOhipModal(true);
  }

  const validOhip = PatientUtils.hasValidOhipCard(patient);
  const invalidOhipText = !validOhip
    ? PatientUtils.showNotificationIfOhipInvalid(patient, false) ||
      `Invalid ${getHealthCardLabel()}`
    : undefined;

  return (
    <>
      <OhipModal setModalOpen={showOhipModal} isModalOpen={ohipModal} patient={patient} />
      <div onClick={openModal} className="clickable flex ohip-element">
        <Item
          title={getHealthCardLabel()}
          text={PatientUtils.getOhipNumber(patient)}
          showSeparator={showSeparator}
          extra={
            <ValidIcon
              value={validOhip}
              labelOk={`Valid ${getHealthCardLabel()}`}
              labelNotOk={invalidOhipText}
            />
          }
        />
      </div>
    </>
  );
};

const PatientPhoto = React.memo(({ patient }) => {
  const nav = useNavStack(Stacks.Master);
  const [linkedAccountModal, showLinkedAccountModal] = useState(false);
  const [showAlertEdit, setShowAlertEdit] = useState(false);

  function openModal() {
    showLinkedAccountModal(true);
  }

  function closeModal() {
    showLinkedAccountModal(false);
  }

  function onClickLinkedAccount(id) {
    let route = RouteUtils.getRoute(RouteUtils.routes().patient, {
      ':patientId': id,
    });
    nav.push(route);
  }

  let { linked_accounts, linked_to_accounts } = patient;

  let showLinkedAccounts =
    (linked_accounts !== undefined && linked_accounts.length > 0) ||
    (linked_to_accounts !== undefined && linked_to_accounts.length > 0);

  return (
    <div className="avatar-wrapper">
      <PatientImage
        showAlertEdit={true}
        hideAlert={false}
        onClickedAlert={() => setShowAlertEdit(true)}
        user={patient}
        size={35}
      />
      {showAlertEdit && (
        <DefaultModal
          className="patient-alert-modal"
          title="Patient Alert"
          wrap={true}
          padding={true}
          content={<EditPatientAlert patient={patient} />}
          isOpen={showAlertEdit}
          onRequestClose={() => setShowAlertEdit(false)}
        />
      )}
      {showLinkedAccounts && (
        <>
          <div className="icon-bubble linked-accounts-toggle" onClick={openModal}>
            <Icon
              src="shuffle"
              width="15px"
              className="blue"
              fontAwesomeStyle="fad"
              showTooltip={true}
              tooltipPlace="right"
            />
          </div>
          {linkedAccountModal && (
            <DefaultModal
              className="modal"
              wrap={true}
              padding={false}
              key="linked-account-modal"
              title="Linked Accounts"
              content={
                <AccountsList
                  patient={patient}
                  activeId={patient.id}
                  onClick={onClickLinkedAccount}
                  allowUnlink={false}
                />
              }
              isOpen={linkedAccountModal}
              onRequestClose={closeModal}
            />
          )}
        </>
      )}
    </div>
  );
});

const DateOfBirth = React.memo(({ patient }) => {
  let birthDateText = PatientUtils.getBirthdate(patient, undefined, true);

  let { date_deceased } = patient;
  if (!date_deceased) return <Item title="DoB" text={birthDateText} />;

  birthDateText = PatientUtils.getBirthdate(patient, 'DD/MM/YYYY', false);

  let age = PatientUtils.getAge(patient);

  let deceasedText = `Born on ${birthDateText} / Deceased on ${CalendarUtils.format(
    date_deceased,
  )} aged ${age}`;
  return (
    <Item
      text=""
      showSeparator={true}
      extra={
        <Icon
          src="skull-crossbones"
          fontAwesomeStyle="fas"
          height="15px"
          width="15px"
          alt={deceasedText}
          className="inline-flex white"
          style={{ position: 'relative', margin: '0' }}
          showTooltip={true}
          tooltipPlace="top"
        />
      }
    />
  );
});

const GenderIcon = React.memo(({ patient }) => {
  let gender = PatientUtils.getGender(patient);
  let icon = gender === 'Male' ? 'mars' : 'venus';
  return (
    <Icon
      src={icon}
      width="15px"
      alt={gender}
      className="inline-block white"
      showTooltip={true}
      tooltipPlace="right"
    />
  );
});

const Item = ({ title, text, showSeparator = true, extra = undefined, tooltip, onClick }) => (
  <>
    <span className={'detail' + (onClick !== undefined ? ' clickable' : '')} onClick={onClick}>
      <span className="item-title">{title ? title + ': ' : ''}</span>
      <DefaultTooltip title={tooltip} open={tooltip ? undefined : false} placement="bottom">
        <span className="bold">{text}</span>
      </DefaultTooltip>
    </span>
    {extra}
    {showSeparator && <span className="separator">|</span>}
  </>
);

const STYLE_OK = {
  display: 'flex',
  marginLeft: '5px',
};
const STYLE_NOT_OK = {
  display: 'flex',
  marginLeft: '5px',
};
const ValidIcon = React.memo(({ value, labelOk, labelNotOk, className = 'white' }) => (
  <>
    {value === true && (
      <Icon
        src={['fas', 'circle-check']}
        className={className}
        alt={labelOk}
        showTooltip={true}
        tooltipPlace="right"
        width="15px"
        style={STYLE_OK}
      />
    )}
    {value !== true && (
      <Icon
        src={['fad', 'circle-exclamation']}
        className="red"
        width="15px"
        iconStyle={{
          '--fa-primary-color': 'white',
          '--fa-secondary-color': 'red',
          '--fa-secondary-opacity': 1.0,
        }}
        alt={labelNotOk}
        showTooltip={true}
        tooltipPlace="right"
        style={STYLE_NOT_OK}
      />
    )}
  </>
));

export default PatientInfoRow;
